import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../api";
import { coverSrc } from "@/lib/cover";
import { useLanguage } from "../context/LanguageContext";
import { trackEvent } from "../lib/analytics";
import { useOwned, openLink } from "../owned";

const FORMAT_LABEL = { ebook: "Ebook", audiobook: "Audiobook", aplikacja: "Aplikacja", subskrypcja: "Abonament" };
// porównanie bez wielkości liter i polskich znaków: „zolw" znajdzie „Żółw"
const norm = (s) => (s || "").toLowerCase().replace(/ł/g, "l").normalize("NFD").replace(/[̀-ͯ]/g, "");

// Wyszukiwarka tytułów: lupa w pasku nawigacji otwiera okno z polem i wynikami na żywo.
// Katalog pobierany raz przy pierwszym otwarciu; szukamy po tytule, autorze i nazwie kategorii.
export function SiteSearch() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const owned = useOwned();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState({});
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const loadedRef = useRef(false);

  // katalog pobierany w tle chwilę po wejściu na stronę — wyniki są od razu, gdy klient otworzy wyszukiwarkę
  const load = () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    api.get("/products").then((r) => setItems(r.data)).catch(() => { loadedRef.current = false; setItems([]); });
    api.get("/categories").then((r) => setCats(Object.fromEntries(r.data.map((c) => [c.slug, c.name])))).catch(() => {});
  };
  useEffect(() => {
    const id = setTimeout(load, 2500);
    return () => clearTimeout(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    load();
    const id = setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => { clearTimeout(id); document.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // skrót „/" otwiera wyszukiwarkę (poza polami tekstowymi)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) || e.target?.isContentEditable) return;
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    if (!items || words.length === 0) return [];
    return items
      .map((p) => {
        const title = norm(p.title);
        const hay = `${title} ${norm(p.author)} ${norm(cats[p.category])} ${norm(FORMAT_LABEL[p.format])}`;
        if (!words.every((w) => hay.includes(w))) return null;
        // tytuł zaczynający się od frazy > fraza w tytule > trafienie w autorze / kategorii
        // posiadane produkty (moja biblioteka) na samej górze wyników
        const score = (owned.has(p.id) ? 0 : 10) + (title.startsWith(words[0]) ? 0 : words.every((w) => title.includes(w)) ? 1 : 2);
        return { p, score };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score || a.p.title.localeCompare(b.p.title, "pl"))
      .slice(0, 8)
      .map((x) => x.p);
  }, [q, items, cats, owned]);

  useEffect(() => { setActive(0); }, [q]);

  // do statystyk: czego klienci szukają i czego nie znajdują (po chwili bez pisania, od 3 znaków)
  useEffect(() => {
    const phrase = q.trim();
    if (phrase.length < 3 || items === null) return undefined;
    const id = setTimeout(() => trackEvent("search", { query: phrase, results: results.length }), 1500);
    return () => clearTimeout(id);
  }, [q, items, results.length]);

  const go = (p) => {
    setOpen(false); setQ("");
    if (owned.has(p.id)) {                   // mam ten produkt: od razu otwieram aplikację albo bibliotekę
      const href = openLink(owned.get(p.id));
      if (href.startsWith("/konto")) navigate(href); else window.location.href = href;
      return;
    }
    navigate(`/produkt/${p.id}`);
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) go(results[active]);
  };

  return (
    <>
      <button
        data-testid="nav-search-button"
        onClick={() => setOpen(true)}
        className="rounded-full glass-panel p-2.5 text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
        aria-label={t("search.open")}
        title={t("search.open")}
      >
        <Search className="h-5 w-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/80 px-4 pt-24 backdrop-blur-sm"
             onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }} data-testid="site-search">
          <div role="dialog" aria-modal="true" aria-label={t("search.open")}
               className="w-full max-w-2xl overflow-hidden rounded-3xl border border-cyan-400/25 bg-slate-900 shadow-2xl shadow-cyan-500/20">
            <div className="flex items-center gap-3 border-b border-slate-700/70 px-5 py-4">
              <Search className="h-5 w-5 shrink-0 text-cyan-300" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("search.placeholder")}
                aria-label={t("search.placeholder")}
                data-testid="site-search-input"
                className="w-full bg-transparent text-base text-slate-100 outline-none placeholder:text-slate-500"
              />
              <button onClick={() => setOpen(false)} aria-label={t("search.close")}
                      className="rounded-full p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="site-search-results">
              {q.trim() === "" && <p className="px-4 py-6 text-center text-sm text-slate-500">{t("search.hint")}</p>}
              {q.trim() !== "" && items === null && <p className="px-4 py-6 text-center text-sm text-slate-500">{t("search.loading")}</p>}
              {q.trim() !== "" && items !== null && results.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-slate-400">
                  <p>{t("search.empty")}</p>
                  <button
                    onClick={() => { setOpen(false); window.dispatchEvent(new Event("open-book-request")); }}
                    className="mt-3 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/10 px-4 py-2 text-xs font-bold text-fuchsia-300"
                  >
                    {t("nav.bookRequest")}
                  </button>
                </div>
              )}
              {results.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => go(p)}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`site-search-result-${p.id}`}
                  className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors ${i === active ? "bg-cyan-500/10" : "hover:bg-slate-800/70"}`}
                >
                  {p.cover_image
                    ? <img src={coverSrc(p)} alt="" loading="lazy" className="h-14 w-10 shrink-0 rounded-md object-cover shadow" />
                    : <div className="h-14 w-10 shrink-0 rounded-md bg-gradient-to-br from-cyan-600 to-blue-700" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-100">{p.title}</div>
                    <div className="truncate text-xs text-slate-400">
                      {p.author}{p.author ? " · " : ""}{FORMAT_LABEL[p.format] || p.format}{cats[p.category] ? ` · ${cats[p.category]}` : ""}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    {owned.has(p.id)
                      ? <span className="rounded-lg bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold text-emerald-300">Masz · Otwórz</span>
                      : p.badge === "wkrotce"
                      ? <span className="text-xs font-semibold text-violet-300">{t("search.soon")}</span>
                      : <span className="text-sm font-bold text-white">{p.price.toFixed(2)} zł</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
